import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import '../styles/Sticker.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const COLORS = ['#fff9b1','#f3d9fa','#b2f2bb','#a5d8ff','#ffd8a8','#ffc9c9','#d0bfff','#99e9f2'];
/* Coordinates stored as a percentage of the board (x = width %, y = height %)
   so the wall looks the same on every device / screen size. */

function LoginPrompt({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem('token', res.data.token);
      onLogin(res.data.token);
    } catch { setError('密码错误'); }
  };
  return (
    <div className="sticker-login">
      <form onSubmit={submit}>
        <h2>Sticker Wall</h2>
        <input type="password" placeholder="密码" value={password}
          onChange={e => setPassword(e.target.value)} autoFocus />
        {error && <p className="sticker-login-error">{error}</p>}
        <button type="submit">进入</button>
      </form>
    </div>
  );
}

// Clicking the read-only <div> swaps it for a <textarea> — a different DOM
// node, so the click can't natively place the caret there. Read the caret
// position from the click while the div (with the same text) is still on
// screen, then replay it onto the textarea once it mounts.
//
// caretRangeFromPoint tried first even though caretPositionFromPoint is the
// newer standard: it's had universal support for far longer (Safari since
// v5), so it's the safer default — caretPositionFromPoint is only reached
// as a fallback for the browsers that never shipped the older one (older
// Firefox).
//
// .sticker-text is a flex container with align-items: center — short text
// in a tall sticker sits in a thin centered band with empty space above and
// below. caretRangeFromPoint only returns a sensible mid-text answer when
// the point actually falls inside that band; anywhere above snaps to 0,
// anywhere below snaps to the end, which is most of the box for a short
// note. Clamp the click into the text's own rendered rect first so a click
// above lands at the start of the nearest line and a click below lands at
// its end, instead of always the very start/end of the whole note.
function caretOffsetFromClick(e) {
  const textNode = e.currentTarget.firstChild;
  let x = e.clientX;
  let y = e.clientY;
  if (textNode && textNode.nodeType === Node.TEXT_NODE) {
    const textRange = document.createRange();
    textRange.selectNodeContents(textNode);
    const r = textRange.getBoundingClientRect();
    if (r.width > 0 || r.height > 0) {
      // +1/-1: landing exactly on the rect's edge pixel is itself ambiguous
      // in some engines (rounds to just outside), so nudge strictly inside.
      x = Math.min(Math.max(x, r.left), r.right - 1);
      y = Math.min(Math.max(y, r.top + 1), r.bottom - 1);
    }
  }
  let range;
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(x, y);
  } else if (document.caretPositionFromPoint) {
    const pos = document.caretPositionFromPoint(x, y);
    if (!pos) return null;
    range = document.createRange();
    range.setStart(pos.offsetNode, pos.offset);
  }
  if (!range || range.startContainer.nodeType !== Node.TEXT_NODE) return null;
  return range.startOffset;
}

function StickerBoard({ token }) {
  const [stickers, setStickers] = useState([]);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [editing, setEditing] = useState(null);
  const boardRef = useRef(null);
  const colorBtnRef = useRef(null);
  const pendingCaretRef = useRef(null);
  const editingTextareaRef = useRef(null);

  // Runs once per actual transition into edit mode (dependency is the sticker
  // id, not "every render") — an inline ref callback re-fires on any re-render
  // while editing (e.g. React StrictMode's dev-only double-invoke), and by
  // then pendingCaretRef would already be spent, dropping the caret to the
  // end. Left unset here on purpose: the next click always overwrites it
  // before the effect reads it again, so there's nothing to clean up.
  useLayoutEffect(() => {
    const el = editingTextareaRef.current;
    if (!editing || !el) return;
    el.focus();
    const pos = pendingCaretRef.current ?? el.value.length;
    el.setSelectionRange(pos, pos);
  }, [editing]);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stickers`, { headers });
      // One-time migration: legacy stickers stored coordinates in pixels
      // (values > 100). Convert them to board percentages using the real
      // measured board size, then persist the corrected values. Skipped on
      // mobile, where the board uses a flow layout and coords are ignored.
      const rect = boardRef.current?.getBoundingClientRect();
      if (rect && rect.width > 768 && rect.height > 400) {
        data.forEach(s => {
          let changed = false;
          if (s.x > 100) {
            s.x = Math.round(Math.min(95, s.x / rect.width * 100) * 10) / 10;
            changed = true;
          }
          if (s.y > 100) {
            s.y = Math.round(Math.min(95, s.y / rect.height * 100) * 10) / 10;
            changed = true;
          }
          if (changed) {
            axios.put(`${API}/stickers/${s.id}`, { x: s.x, y: s.y }, { headers }).catch(() => {});
          }
        });
      }
      setStickers(data);
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Close color picker on outside click
  useEffect(() => {
    if (!showColorPicker) return;
    const handler = (e) => {
      if (!e.target.closest('.sticker-color-picker') && !e.target.closest('.sticker-color-toggle')) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColorPicker]);

  /* ── Create sticker on board click ── */
  const handleBoardClick = async (e) => {
    if (e.target.closest('.sticker-note')) return;
    const rect = boardRef.current.getBoundingClientRect();
    // both x and y as % of the board
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top + boardRef.current.scrollTop) / rect.height) * 100;
    const x = Math.max(0, Math.min(90, Math.round(xPct * 10) / 10));
    const y = Math.max(0, Math.min(95, Math.round(yPct * 10) / 10));
    const sticker = { text: '', color: selectedColor, x, y, createdAt: new Date().toISOString() };
    try {
      const { data } = await axios.post(`${API}/stickers`, sticker, { headers });
      setStickers(prev => [...prev, data]);
      setEditing(data.id);
    } catch (e) { console.error(e); }
  };

  /* ── Update sticker ── */
  const updateField = async (id, field, value) => {
    setStickers(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    try {
      await axios.put(`${API}/stickers/${id}`, { [field]: value }, { headers });
    } catch (e) { console.error(e); }
  };

  /* ── Delete sticker ── */
  const remove = async (id) => {
    setStickers(prev => prev.filter(s => s.id !== id));
    try {
      await axios.delete(`${API}/stickers/${id}`, { headers });
    } catch (e) { console.error(e); }
  };

  /* ── Export ── */
  const exportData = () => {
    const blob = new Blob([JSON.stringify(stickers, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stickers-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Drag ── */
  const savePos = useCallback(async (id, x, y) => {
    try { await axios.put(`${API}/stickers/${id}`, { x, y }, { headers }); } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startDrag = (e, id) => {
    e.stopPropagation();
    const rect = boardRef.current.getBoundingClientRect();
    const s = stickers.find(s => s.id === id);
    const startPxX = (s.x / 100) * rect.width + rect.left;
    const startPxY = (s.y / 100) * rect.height + rect.top;
    const offX = e.clientX - startPxX;
    const offY = e.clientY - startPxY;
    let lastX = s.x, lastY = s.y;

    const move = (cx, cy) => {
      const rawXPct = ((cx - rect.left - offX) / rect.width) * 100;
      const rawYPct = ((cy - rect.top - offY) / rect.height) * 100;
      lastX = Math.max(0, Math.min(90, Math.round(rawXPct * 10) / 10));
      lastY = Math.max(0, Math.min(95, Math.round(rawYPct * 10) / 10));
      setStickers(prev => prev.map(s => s.id === id ? { ...s, x: lastX, y: lastY } : s));
    };

    const onMouseMove = (ev) => move(ev.clientX, ev.clientY);
    const onTouchMove = (ev) => { ev.preventDefault(); move(ev.touches[0].clientX, ev.touches[0].clientY); };
    const onEnd = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
      savePos(id, lastX, lastY);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  const startTouchDrag = (e, id) => {
    const t = e.touches[0];
    startDrag({ clientX: t.clientX, clientY: t.clientY, stopPropagation: () => {} }, id);
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="sticker-page">
      <div className="sticker-toolbar">
        <div className="sticker-color-toggle-wrap">
          <button className="sticker-color-toggle" ref={colorBtnRef}
            style={{ background: selectedColor }}
            onClick={() => setShowColorPicker(!showColorPicker)} />
          {showColorPicker && (
            <div className="sticker-color-picker">
              {COLORS.map(c => (
                <span key={c}
                  className={`sticker-color-dot${c === selectedColor ? ' selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => { setSelectedColor(c); setShowColorPicker(false); }} />
              ))}
            </div>
          )}
        </div>
        <button className="sticker-export-btn" onClick={exportData}>Export</button>
        <span className="sticker-toolbar-hint">Click empty space to add</span>
      </div>
      <div className="sticker-board-wrap">
        <div className="sticker-board" ref={boardRef} onClick={handleBoardClick}>
          {stickers.map(s => (
            <div key={s.id} className="sticker-note" data-sticker-id={s.id}
              style={{ left: `${s.x}%`, top: `${s.y}%`, background: s.color }}>
              <div className="sticker-handle"
                onMouseDown={e => startDrag(e, s.id)}
                onTouchStart={e => startTouchDrag(e, s.id)}>
                ⠿
              </div>
              <button className="sticker-delete" onClick={(e) => { e.stopPropagation(); remove(s.id); }}>×</button>
              {editing === s.id ? (
                <textarea className="sticker-text-input"
                  ref={editingTextareaRef}
                  value={s.text}
                  onChange={e => updateField(s.id, 'text', e.target.value)}
                  onBlur={() => setEditing(null)}
                  onClick={e => e.stopPropagation()} />
              ) : (
                <div className="sticker-text" onClick={e => {
                  e.stopPropagation();
                  pendingCaretRef.current = s.text ? caretOffsetFromClick(e) : null;
                  setEditing(s.id);
                }}>
                  {s.text || 'click to edit'}
                </div>
              )}
              <div className="sticker-note-footer">
                <div className="sticker-colors">
                  {COLORS.map(c => (
                    <button key={c} className="sticker-mini-color" style={{ background: c }}
                      onClick={e => { e.stopPropagation(); updateField(s.id, 'color', c); }} />
                  ))}
                </div>
                <span className="sticker-time">{fmtTime(s.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Sticker() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  if (!token) return <LoginPrompt onLogin={setToken} />;
  return <StickerBoard token={token} />;
}
